import { mutationOptions } from '@tanstack/react-query';

import type { NotificationsConsentResponse } from '@/api/booking/getShopNotificationConsentQuery';
import { getShopNotificationConsentQuery } from '@/api/booking/getShopNotificationConsentQuery';
import apiClient from '@/constants/http';
import { Logger } from '@/utils/logging';

import type {
  UpdateNotificationConsentPayload,
  UpdateNotificationConsentResponse,
} from './types';

async function updateNotificationConsent({
  shopId,
  customerId,
  ...data
}: UpdateNotificationConsentPayload) {
  const response = await apiClient.put<UpdateNotificationConsentResponse>(
    `/v1/customer/${customerId}/shop/${shopId}/notifications-consent`,
    data,
  );

  return response.data;
}

export const updateNotificationConsentMutation = () =>
  mutationOptions<
    UpdateNotificationConsentResponse,
    Error,
    UpdateNotificationConsentPayload
  >({
    mutationKey: ['update-notification-consent'],
    mutationFn: (variables, ctx) => {
      let customerId = variables.customerId;

      if (!customerId) {
        // Get customer ID from consent query response. Prefix-match on the
        // stable part of the key (['shop', shopId, 'notifications-consent']) so
        // we find the cached consent regardless of which identity fields were
        // used to fetch it.
        const queryPayload = {
          customer: {},
          enabled: false,
          shopId: variables.shopId,
        };

        const queryKey = getShopNotificationConsentQuery(
          queryPayload,
        ).queryKey.slice(0, 3);

        const foundQueries =
          ctx.client.getQueriesData<NotificationsConsentResponse>({
            exact: false,
            queryKey: queryKey,
          });

        const [_, shopNotificationConsentData] = foundQueries.at(-1) || [];

        customerId = shopNotificationConsentData?.customerId;
      }

      if (!customerId) {
        Logger.error(
          'Customer ID not found on update notification consent mutation',
        );

        throw new Error(
          'Customer ID not found on update notification consent mutation',
        );
      }

      return updateNotificationConsent({
        ...variables,
        customerId,
      });
    },
  });
